import { useQuery } from '@apollo/client';
import { LanguagesForProduct_TranslationType } from '__generated__/graphql-types';

import GetCourseLanguages from 'bundles/common/queries/GetCourseLanguages.graphql';
import type {
  LanguagesForCourseQuery,
  LanguagesForCourseQueryVariables,
} from 'bundles/common/queries/__generated__/GetCourseLanguages';

type TranslatedLanguage = {
  languageCode: string;
  translationType: string;
};

export type CourseLanguages = {
  originalLanguage: string;
  autoTranslations: string[];
  humanTranslations: string[];
  subtitles: string[];
  translatedLanguages?: TranslatedLanguage[];
};

const useGetCourseLanguages = (courseId?: string): { loading: boolean } & CourseLanguages => {
  const data = useQuery<LanguagesForCourseQuery, LanguagesForCourseQueryVariables>(GetCourseLanguages, {
    context: { clientName: 'gatewayGql' },
    skip: !courseId,
    variables: {
      // This is intentional as we are verifying that courseId exists, otherwise we skip
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      input: { courseId: courseId! },
    },
    ssr: false,
  });

  const loading = data.loading;
  let originalLanguage = '';
  let autoTranslations: string[] = [];
  let humanTranslations: string[] = [];
  let subtitles: string[] = [];
  let translatedLanguages: TranslatedLanguage[] = [];
  const queryData = data.data?.LanguagesForProduct?.queryByCourse;

  if (queryData?.__typename === 'LanguagesForProduct_QueryByCourseSuccess') {
    [originalLanguage] = queryData.availableLanguages.primaryLanguageCodes;
    subtitles = queryData.availableLanguages.subtitleLanguageCodes;

    /**
     * Update to the API where humand and machine translated languages are nested inside translatedLanguages field.
     * Example:  
     * "translatedLanguages": [
          {
            "languageCode": "es",
            "translationType": "HUMAN"
          },
          {
            "languageCode": "fr",
            "translationType": "MACHINE"
          },
        ]
     */
    translatedLanguages = queryData.availableLanguages.translatedLanguages;
    humanTranslations = (translatedLanguages || [])
      .filter(({ translationType }) => translationType === LanguagesForProduct_TranslationType.Human)
      .map(({ languageCode }) => languageCode);

    autoTranslations = (translatedLanguages || [])
      .filter(({ translationType }) => translationType === LanguagesForProduct_TranslationType.Machine)
      .map(({ languageCode }) => languageCode);
  }

  return {
    loading,
    originalLanguage,
    autoTranslations,
    humanTranslations,
    subtitles,
    translatedLanguages,
  };
};

export { useGetCourseLanguages };
